import React, { useState, useEffect } from 'react';
import { useStory } from '../../contexts/StoryContext';
import { storyService } from '../../services/storyService';
import { logger } from '../../services/utils';
import './ShareToggle.css';

/**
 * ShareToggle component for toggling the sharing status of a story
 * Uses the standardized data format where properties are accessed through
 * the data property of the story object.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.story - The story object with data property
 * @param {function} props.onToggleComplete - Callback when toggle is complete
 */
const ShareToggle = ({ story, onToggleComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const { setError } = useStory();
  
  // Access properties directly from the story object (standardized unwrapped format)
  const storyData = story; // Story data is now directly on the story object
  const storyId = story?.id;
  
  // Add useEffect to log when story prop changes
  useEffect(() => {
    if (story && storyData) {
      logger.debug('ShareToggle', `Story prop received:`, {
        id: storyId || 'missing',
        title: storyData.title || 'untitled',
        isOwner: storyData.user_is_owner,
        hasValidId: !!storyId
      });
    } else {
      logger.debug('ShareToggle', 'No story data received');
    }
  }, [story?.id]); // Only re-run if story ID changes, not on entire story object

  // Only render if story and story data exist
  if (!story || !storyData) {
    return null;
  }
  
  // Log a warning if story is missing ID
  if (!storyId) {
    logger.error('ShareToggle', 'Story is missing ID!', { 
      storyKeys: Object.keys(story),
      dataKeys: storyData ? Object.keys(storyData) : [],
      storyType: typeof story,
      storyData: JSON.stringify(story).substring(0, 200) + '...'
    });
  }
  
  // Strict check: only show if user_is_owner is explicitly true
  // This ensures we don't show the toggle when ownership is undefined or false
  if (storyData.user_is_owner !== true) {
    return null; // Only show for confirmed story owners
  }
  
  const toggleSharing = async () => {
    if (isLoading) return;
    
    // Defensive check for story ID
    if (!storyId) {
      logger.error('ShareToggle', 'Cannot toggle sharing: story ID is missing');
      setError('Cannot share story: missing story ID');
      return;
    }
    
    setIsLoading(true);
    try {
      // Use the storyService to toggle sharing
      const response = await storyService.toggleSharing(storyId);
      
      // Log the full response for debugging
      logger.debug('ShareToggle', 'Toggle sharing response:', {
        full: JSON.stringify(response),
        responseType: typeof response,
        responseKeys: response ? Object.keys(response) : [],
        isPublic: response?.is_public,
        shareUrl: response?.share_url,
        id: response?.id,
        hasId: !!response?.id,
        storyId: storyId
      });
      
      // Show share dialog if the story is now public
      if (response.is_public) {
        setShowShareDialog(true);
      }
      
      // Only use the response data from the API
      // We expect the response to include all required fields including the ID
      logger.debug('ShareToggle', 'Response from API:', {
        responseId: response?.id,
        hasId: !!response?.id,
        responseKeys: Object.keys(response || {})
      });
      
      // If ID is missing from the response, log an error
      if (!response.id) {
        logger.error('ShareToggle', 'API response missing ID field', {
          responseData: JSON.stringify(response).substring(0, 200)
        });
      }
      
      // Call the callback with the response data directly
      if (typeof onToggleComplete === 'function') {
        // Pass the API response directly to the parent component
        onToggleComplete(response);
      }
    } catch (error) {
      console.error('Failed to toggle sharing', error);
      setError('Failed to toggle sharing status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const copyShareLink = () => {
    if (storyData.share_url) {
      navigator.clipboard.writeText(storyData.share_url)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => console.error('Failed to copy: ', err));
    }
  };
  
  const closeShareDialog = () => {
    setShowShareDialog(false);
  };
  
  // Handle clicks outside the dialog to close it
  const handleBackdropClick = (e) => {
    if (e.target.className === 'share-dialog') {
      closeShareDialog();
    }
  };
  
  return (
    <div className="share-toggle-container">
      {storyData.is_public && storyData.share_url && (
        <button 
          className="copy-link-button" 
          onClick={copyShareLink}
          title="Copy share link"
        >
          <span>{copied ? '✓' : '📋'}</span>
          <span className="sr-only">Copy Link</span>
        </button>
      )}
      
      <button 
        className={`share-toggle-button ${storyData.is_public ? 'share-toggle-active' : ''}`}
        onClick={toggleSharing}
        disabled={isLoading}
      >
        <span>🔗 {storyData.is_public ? 'Shared' : 'Share'}</span>
      </button>
      
      {showShareDialog && (
        <div className="share-dialog" onClick={handleBackdropClick}>
          <div className="share-dialog-content">
            <button 
              className="share-dialog-close-x"
              onClick={closeShareDialog}
              aria-label="Close dialog"
            >
              ×
            </button>
            <p className="share-dialog-message">Your story is now public. Anyone can read it at this link:</p>
            <div className="share-url-container">
              <div className="share-url-text">{storyData.share_url || ''}</div>
              <button 
                onClick={copyShareLink}
                className="share-copy-button"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ShareToggle);